import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "text-xs text-gray-700 dark:text-gray-500" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("h5", { class: "font-bold dark:text-gray-300" }, "Set initial liquidity", -1)
const _hoisted_3 = { class: "dark:text-gray-400 font-medium" }
const _hoisted_4 = { class: "text-sm pl-2" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "p-3 border rounded-lg" }
const _hoisted_7 = { class: "text-sm" }
const _hoisted_8 = ["disabled"]

import { onMounted, onBeforeMount, ref, computed } from 'vue';
import { bnum } from '@/lib/utils';
import useWeb3 from '@/services/web3/useWeb3';
import useTokens from '@/composables/useTokens';
import usePoolCreation from '@/composables/pools/usePoolCreation';
import useNumbers, { FNumFormats } from '@/composables/useNumbers';

import TokenInput from '@/components/inputs/TokenInput/TokenInput.vue';
import AnimatePresence from '@/components/animate/AnimatePresence.vue';

import { useI18n } from 'vue-i18n';
import { isGreaterThan } from '@/lib/utils/validations';


export default _defineComponent({
  emits: ['update:height'],
  setup(__props, { emit }) {



/**
 * STATE
 */
const isOptimised = ref(false);
const cardWrapper = ref<HTMLElement>();

/**
 * COMPOSBALES
 */
const { userNetworkConfig } = useWeb3();
const { balanceFor, priceFor, nativeAsset, wrappedNativeAsset } = useTokens();
const { fNum2 } = useNumbers();
const {
  seedTokens,
  tokensList,
  optimisedLiquidity,
  totalLiquidity,
  scaledLiquidity,
  manuallySetToken,
  autoOptimiseBalances,
  currentLiquidity,
  isWethPool,
  useNativeAsset,
  poolLiquidity,
  goBack,
  updateManuallySetToken,
  proceed,
  clearAmounts,
  setAmountsToMaxBalances,
  saveState
} = usePoolCreation();
const { t } = useI18n();

const tokenAddresses = ref([] as string[]);

/**
 * COMPUTED
 */
const areAmountsMaxed = computed(() => {
  const isMaxed = seedTokens.value.every(t =>
    bnum(t.amount).eq(balanceFor(t.tokenAddress))
  );
  return isMaxed;
});

const isExceedingWalletBalance = computed(() => {
  // need to perform rounding here as JS cuts off those
  // really long numbers which makes it impossible to compare
  const isExceeding = tokenAddresses.value.some((t, i) =>
    bnum(seedTokens.value[i].amount).gt(balanceFor(t))
  );
  return isExceeding;
});

const arbitrageDelta = computed(() => {
  let totalPctDelta = bnum(0);
  for (const token of seedTokens.value) {
    const initialPct = bnum(token.amount)
      .times(priceFor(token.tokenAddress))
      .div(poolLiquidity.value);
    const expectedPct = token.weight / 100;
    const delta = initialPct.minus(expectedPct).abs();
    totalPctDelta = totalPctDelta.plus(delta);
  }
  return {
    delta: totalPctDelta,
    value: totalPctDelta.times(poolLiquidity.value)
  };
});

const hasZeroAmount = computed(() => {
  return seedTokens.value.some(seedToken => bnum(seedToken.amount).eq(0));
});

/**
 * LIFECYCLE
 */
onBeforeMount(() => {
  tokenAddresses.value = [...tokensList.value];
  if (isWethPool.value) setNativeAssetIfRequired();
});

onMounted(() => {
  optimiseLiquidity();
  scaleLiquidity();
});

/**
 * METHODS
 */
function optimiseLiquidity(force = false) {
  if (manuallySetToken.value && !force) return;
  isOptimised.value = true;

  for (const token of seedTokens.value) {
    token.amount = optimisedLiquidity.value[token.tokenAddress].balanceRequired;
  }
}

function scaleLiquidity() {
  if (!autoOptimiseBalances.value || !manuallySetToken.value) return;

  for (const token of seedTokens.value) {
    if (token.tokenAddress !== manuallySetToken.value) {
      token.amount = scaledLiquidity.value[token.tokenAddress].balanceRequired;
    }
  }
}

function toggleAutoOptimise() {
  autoOptimiseBalances.value = !autoOptimiseBalances.value;
  checkLiquidityScaling();
}

function checkLiquidityScaling() {
  if (!autoOptimiseBalances.value) return;

  scaleLiquidity();
}

function handleMax() {
  setAmountsToMaxBalances();
  isOptimised.value = false;
}

function handleAmountChange(tokenAddress) {
  updateManuallySetToken(tokenAddress);
  checkLiquidityScaling();
  isOptimised.value = false;
}

function handleAddressChange(newAddress: string): void {
  useNativeAsset.value = newAddress === nativeAsset.address;
}

function tokenOptions(index: number): string[] {
  if (tokenAddresses.value[index] === wrappedNativeAsset.value.address)
    return [wrappedNativeAsset.value.address, nativeAsset.address];
  if (tokenAddresses.value[index] === nativeAsset.address)
    return [nativeAsset.address, wrappedNativeAsset.value.address];
  return [];
}

// If useNativeAsset is set, or ETH has a higher balance than WETH, then use it for the input.
function setNativeAssetIfRequired(): void {
  const nativeAssetBalance = balanceFor(nativeAsset.address);
  const wrappedNativeAssetBalance = balanceFor(
    wrappedNativeAsset.value.address
  );

  if (
    useNativeAsset.value ||
    bnum(nativeAssetBalance).gt(wrappedNativeAssetBalance)
  ) {
    tokenAddresses.value = tokenAddresses.value.map(address => {
      if (address == wrappedNativeAsset.value.address) {
        return nativeAsset.address;
      }
      return address;
    });
  }
}

function handleClearAll() {
  clearAmounts();
  isOptimised.value = false;
}

function onAlertMountChange() {
  emit('update:height', {
    height: cardWrapper.value?.offsetHeight || 0
  });
}

function saveAndProceed() {
  saveState();
  proceed();
}

return (_ctx: any,_cache: any) => {
  const _component_BalIcon = _resolveComponent("BalIcon")!
  const _component_BalStack = _resolveComponent("BalStack")!
  const _component_BalTooltip = _resolveComponent("BalTooltip")!
  const _component_BalToggle = _resolveComponent("BalToggle")!
  const _component_BalAlert = _resolveComponent("BalAlert")!
  const _component_BalBtn = _resolveComponent("BalBtn")!
  const _component_BalCard = _resolveComponent("BalCard")!

  return (_openBlock(), _createElementBlock("div", { ref: cardWrapper }, [
    _createVNode(_component_BalCard, {
      shadow: "xl",
      noBorder: ""
    }, {
      default: _withCtx(() => [
        _createVNode(_component_BalStack, { vertical: "" }, {
          default: _withCtx(() => [
            _createVNode(_component_BalStack, {
              vertical: "",
              spacing: "xs"
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", _hoisted_1, _toDisplayString(_unref(userNetworkConfig)?.name), 1),
                _createVNode(_component_BalStack, {
                  horizontal: "",
                  spacing: "xs",
                  align: "center"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("button", {
                      onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(goBack) && _unref(goBack)(...args))),
                      class: "text-blue-500 hover:text-blue-700 flex"
                    }, [
                      _createVNode(_component_BalIcon, {
                        class: "flex",
                        name: "chevron-left"
                      })
                    ]),
                    _hoisted_2
                  ]),
                  _: 1
                }),
                _createVNode(AnimatePresence, {
                  isVisible: isOptimised.value,
                  unmountInstantly: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_BalStack, {
                      horizontal: "",
                      align: "center",
                      spacing: "sm",
                      class: "border rounded-lg p-2 mt-2"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_BalIcon, {
                          name: "zap",
                          size: "sm",
                          class: "mt-1 text-gray-500"
                        }),
                        _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('optimizedPrefilled')), 1),
                        _createElementVNode("button", {
                          onClick: handleClearAll,
                          class: "text-sm font-medium text-gray-400 hover:text-blue-500"
                        }, " Clear all ")
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["isVisible"])
              ]),
              _: 1
            }),
            _createVNode(_component_BalStack, { vertical: "" }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tokenAddresses.value, (address, i) => {
                  return (_openBlock(), _createBlock(TokenInput, {
                    amount: _unref(seedTokens)[i].amount,
                    "onUpdate:amount": [($event: any) => (_unref(seedTokens)[i].amount = $event), ($event: any) => (handleAmountChange(address))],
                    address: tokenAddresses.value[i],
                    "onUpdate:address": [
                      ($event: any) => (tokenAddresses.value[i] = $event),
                      _cache[1] || (_cache[1] = ($event: any) => (handleAddressChange($event)))
                    ],
                    fixedToken: "",
                    key: i,
                    weight: _unref(seedTokens)[i].weight / 100,
                    name: `initial-token-${_unref(seedTokens)[i].tokenAddress}`,
                    options: tokenOptions(i),
                    rules: [_unref(isGreaterThan)(0)]
                  }, null, 8, ["amount", "onUpdate:amount", "address", "onUpdate:address", "weight", "name", "options", "rules"]))
                }), 128))
              ]),
              _: 1
            }),
            _createVNode(_component_BalStack, {
              horizontal: "",
              spacing: "sm",
              align: "center"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", null, [
                  _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('autoOptimiseLiquidityToggle.label')), 1),
                  _createVNode(_component_BalTooltip, { width: "64" }, {
                    activator: _withCtx(() => [
                      _createVNode(_component_BalIcon, {
                        name: "info",
                        size: "xs",
                        class: "text-gray-400 ml-1 flex"
                      })
                    ]),
                    default: _withCtx(() => [
                      _createElementVNode("div", {
                        innerHTML: _unref(t)('autoOptimiseLiquidityToggle.tooltip')
                      }, null, 8, _hoisted_5)
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", null, [
                  _createVNode(_component_BalToggle, {
                    name: "autoOptimise",
                    checked: _unref(autoOptimiseBalances),
                    onToggle: toggleAutoOptimise
                  }, null, 8, ["checked"])
                ])
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_6, [
              _createVNode(_component_BalStack, {
                horizontal: "",
                justify: "between"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_BalStack, {
                    vertical: "",
                    spacing: "none"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("h6", null, _toDisplayString(_unref(t)('total')), 1),
                      _createVNode(_component_BalStack, {
                        horizontal: "",
                        spacing: "xs",
                        class: "font-medium"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)('available')) + ": " + _toDisplayString(_unref(fNum2)(_unref(totalLiquidity).toString(), _unref(FNumFormats).fiat)), 1),
                          _createElementVNode("button", {
                            disabled: _unref(areAmountsMaxed),
                            class: _normalizeClass([
                    'text-sm font-semibold3',
                    {
                      'text-gray-400 dark:text-gray-600': _unref(areAmountsMaxed),
                      'text-blue-500 hover:text-blue-50': !_unref(areAmountsMaxed)
                    }
                  ]),
                            onClick: handleMax
                          }, _toDisplayString(_unref(areAmountsMaxed) ? _unref(t)('maxed') : _unref(t)('max')), 11, _hoisted_8)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_BalStack, {
                    vertical: "",
                    spacing: "none"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("h6", null, _toDisplayString(_unref(fNum2)(_unref(currentLiquidity).toString(), _unref(FNumFormats).fiat)), 1),
                      _createVNode(AnimatePresence, {
                        isVisible: !isOptimised.value,
                        onOnPresence: onAlertMountChange,
                        onOnExit: onAlertMountChange,
                        unmountInstantly: ""
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("button", {
                            onClick: _cache[2] || (_cache[2] = ($event: any) => (optimiseLiquidity(true))),
                            class: "bg-clip-text text-sm text-transparent font-medium bg-gradient-to-tr from-blue-500 to-pink-500  hover:from-blue-800 hover:to-pink-800"
                          }, _toDisplayString(_unref(t)('optimize')), 1)
                        ]),
                        _: 1
                      }, 8, ["isVisible"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _createVNode(AnimatePresence, {
              isVisible: _unref(arbitrageDelta).delta > 0.05,
              onOnPresence: onAlertMountChange,
              onOnExit: onAlertMountChange,
              unmountInstantly: ""
            }, {
              default: _withCtx(() => [
                _createVNode(_component_BalAlert, {
                  type: "warning",
                  title: 
              _unref(t)('createAPool.arbTitle', [
                _unref(fNum2)(_unref(arbitrageDelta).value.toString(), _unref(FNumFormats).fiat),
                _unref(fNum2)(_unref(arbitrageDelta).delta.toString(), _unref(FNumFormats).percent)
              ])
            
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('createAPool.arbReason')), 1)
                  ]),
                  _: 1
                }, 8, ["title"])
              ]),
              _: 1
            }, 8, ["isVisible"]),
            _createVNode(_component_BalBtn, {
              disabled: _unref(isExceedingWalletBalance) || _unref(hasZeroAmount),
              onClick: saveAndProceed,
              block: "",
              color: "gradient"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('preview')), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 512))
}
}

})